import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
    BookOpen,
    Bot,
    Building2,
    Palette,
    CalendarCheck,
    CalendarClock,
    GitBranch,
    CalendarDays,
    CalendarRange,
    ChartNoAxesColumn,
    ChartSpline,
    ClipboardList,
    Clock,
    CreditCard,
    FileText,
    Gauge,
    GaugeCircle,
    Kanban,
    Inbox,
    Layers,
    LayoutDashboard,
    Link2,
    ClipboardCheck,
    ListChecks,
    Radio,
    Megaphone,
    Phone,
    MessageSquareText,
    MessagesSquare,
    Plug,
    Rocket,
    Send,
    Settings,
    ShieldCheck,
    Sparkles,
    Sun,
    Target,
    Handshake,
    Ticket,
    TrendingUp,
    Users,
    UserCircle2,
    UserPlus,
    UsersRound,
    Workflow,
} from 'lucide-react';
import type { ComponentType, CSSProperties } from 'react';

import { GoogleAdsIcon } from '@/components/shared/brand-icons/google-ads-icon';
import { MetaIcon } from '@/components/shared/brand-icons/meta-icon';

import { TagPlusIcon } from '@/components/ui/tag';
import { NavMain } from '@/components/layout/navbar/nav-main';
import { TeamSwitcher } from '@/components/layout/navbar/org-switcher';
import { filterNavItems } from '@/components/layout/navbar/filter-nav-items';
import { useModuleNavItems } from '@/components/layout/navbar/use-module-nav-items';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarRail,
    SidebarSeparator,
} from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { ROUTES } from '@/router/constant/routes';
import { usePermissions } from '@/hooks/use-permissions';
import { IS_AGENCY_MODULE_ENABLED } from '@/features/agency/feature-flag';
import { useMyAgency, useResolvedBranding } from '@/features/agency/hooks/use-agency';
import { useOnboardingStatus } from '@/features/onboarding/hooks/use-onboarding-status';
import { PERMISSION } from '@/features/users-roles/constants';
import type { Permission } from '@/features/users-roles/constants';

/**
 * Anything the nav can draw as a row icon: a lucide glyph, or a full-colour
 * brand mark (Google Ads, Meta) that takes the same `className`/`style`.
 */
export type NavIcon = ComponentType<{ className?: string; style?: CSSProperties }>;

export type NavSubItem = {
    title: string;
    /** Absent only when the row is a collapsible group — see `items`. */
    url?: string;
    icon?: NavIcon;
    /** Accent color for the icon (dynamic custom-module entries). */
    iconColor?: string;
    badge?: string;
    requiredPermission?: Permission;
    /** Show if the user has ANY of these permissions (OR logic). */
    anyOfPermissions?: Permission[];
    /**
     * Present when this row folds children instead of navigating — a
     * collapsible menu group nested inside a labelled section (e.g. "Campaigns"
     * under SEND). Rendered by the same `NavGroupItem` that draws
     * top-level groups, so both levels behave identically.
     */
    items?: NavSubItem[];
    /** Accordion key for a nested group; required whenever `items` is set. */
    collapseKey?: string;
};
export type NavItem = {
    title: string;
    url?: string;
    icon?: NavIcon;
    items?: NavSubItem[];
    isSection: boolean;
    /** Collapsible menu group (parent row + nested children), not a labeled section. */
    isGroup?: boolean;
    requiredPermission?: Permission;
    /** Show if the user has ANY of these permissions (OR logic). */
    anyOfPermissions?: Permission[];
    /** When set, the section is collapsible; the key persists its open state. */
    collapseKey?: string;
};

export function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
    const { t } = useTranslation();
    const { hasPermission, hasAnyPermission, isLoading: permissionsLoading } = usePermissions();
    const { section: modulesSection } = useModuleNavItems();
    const { data: onboarding } = useOnboardingStatus();
    // Agency console entry — gated on agency MEMBERSHIP, not org permissions:
    // the agency layer sits above workspaces, so whatever role the user holds
    // in the current workspace is irrelevant to whether they run an agency.
    const { data: myAgency } = useMyAgency();
    // §51 white-label: when this workspace inherits its agency's brand, the
    // footer lockup shows the AGENCY, not AutoCampaign.
    const { data: resolvedBranding } = useResolvedBranding();

    const canDoAnyOnboardingStep =
        !!onboarding &&
        onboarding.steps.some((s) => hasPermission(s.requiredPermission as Permission));
    const showOnboarding =
        !!onboarding &&
        !onboarding.setupComplete &&
        !onboarding.userState.celebrated &&
        canDoAnyOnboardingStep;

    // Plain-words menu: a short unlabelled lead-in (My dashboards / Set-up
    // checklist), then one labelled section per part of a working day — who the
    // leads are (PEOPLE), talking to them (TALK TO CUSTOMERS), the appointments
    // that come out of it (BOOKINGS), what you send (SEND), what runs by itself
    // (AUTOMATE), what it all added up to (RESULTS), and ACCOUNT last.
    //
    // Every heading and row is named the way a home-service owner would say it;
    // the internal words (CRM, Opportunities, Segments, Lead Capture, Insights,
    // Modules) are gone from the UI but every page is still exactly where it
    // was — same route, same group, same permission. `collapseKey`s are
    // deliberately NOT renamed: they key the persisted open state, so touching
    // one would silently reopen a different fold for everyone.
    //
    // Sections hold either plain rows or collapsible groups — both render
    // through `NavMain`, so a group behaves the same nested here as it did at
    // the top level.
    const allRoutes: NavItem[] = React.useMemo(
        () => [
            {
                // First row on purpose: Today is the landing route and the
                // page a lead-response business opens every morning. The
                // dashboard moved below it, and onto `/dashboards`.
                isSection: false,
                title: t('nav.today', 'Today'),
                url: ROUTES.TODAY,
                icon: Sun,
                requiredPermission: 'dashboard:read',
            },
            {
                isSection: false,
                title: t('nav.dashboard', 'My dashboards'),
                url: ROUTES.DASHBOARD,
                icon: LayoutDashboard,
                requiredPermission: 'dashboard:read',
            },
            ...(showOnboarding
                ? [
                      {
                          isSection: false,
                          title: `${t('onboarding.navItem', 'Set-up checklist')} · ${onboarding?.progressPercent ?? 0}%`,
                          url: ROUTES.GETTING_STARTED,
                          icon: Rocket,
                          requiredPermission: PERMISSION.DASHBOARD_READ,
                      },
                  ]
                : []),
            // Agency console is withdrawn — see `IS_AGENCY_MODULE_ENABLED`.
            // These items carry no `requiredPermission`, so `filterNavItems`
            // never touches them; this check is the only thing hiding them.
            ...(IS_AGENCY_MODULE_ENABLED && myAgency
                ? [
                      {
                          isSection: false,
                          title: t('agency.nav.agency', 'My Agency'),
                          url: ROUTES.AGENCY,
                          icon: Building2,
                      },
                      {
                          isSection: false,
                          title: t('agency.nav.setups', 'Setups'),
                          url: ROUTES.AGENCY_SETUPS,
                          icon: Layers,
                      },
                      {
                          isSection: false,
                          title: t('agency.nav.branding', 'Branding'),
                          url: ROUTES.AGENCY_BRANDING,
                          icon: Palette,
                      },
                  ]
                : []),
            {
                isSection: true,
                // Who the leads and customers are. One section, three collapsible
                // groups in the same shape as SEND's Campaigns — icon, chevron,
                // tree-rail children — and none of them is a route. They join the
                // one shared accordion, so opening one closes the others. Every
                // leaf keeps its route, icon and permission; only the words moved.
                title: t('nav.sections.crm', 'People'),
                items: [
                    {
                        title: t('nav.headings.audience', 'Contacts'),
                        icon: UsersRound,
                        collapseKey: 'crm-audience',
                        items: [
                            {
                                title: t('nav.contacts', 'All contacts'),
                                url: ROUTES.CONTACTS,
                                icon: Users,
                                requiredPermission: 'contacts:read',
                            },
                            {
                                // Saved groups of contacts. "Segments" is the
                                // word the industry uses; "Lists" is the word
                                // the customer uses.
                                title: t('nav.segments', 'Lists'),
                                url: ROUTES.SEGMENTS,
                                icon: Layers,
                                requiredPermission: 'segments:read',
                            },
                            {
                                title: t('nav.tags', 'Tags'),
                                url: ROUTES.TAGS,
                                icon: TagPlusIcon,
                                requiredPermission: PERMISSION.TAGS_READ,
                            },
                        ],
                    },
                    {
                        title: t('nav.headings.opportunities', 'Deals'),
                        icon: Handshake,
                        collapseKey: 'crm-opportunities',
                        items: [
                            {
                                title: t('nav.opportunitiesAll', 'All deals'),
                                url: ROUTES.OPPORTUNITIES,
                                icon: Kanban,
                                requiredPermission: PERMISSION.OPPORTUNITIES_READ,
                            },
                            {
                                title: t('nav.opportunitiesMine', 'My deals'),
                                url: `${ROUTES.OPPORTUNITIES}?mine=1`,
                                icon: UserCircle2,
                                requiredPermission: PERMISSION.OPPORTUNITIES_READ,
                            },
                            {
                                title: t('nav.opportunityReports', 'Deal reports'),
                                url: ROUTES.OPPORTUNITY_REPORTS,
                                icon: ChartNoAxesColumn,
                                requiredPermission: PERMISSION.OPPORTUNITIES_READ,
                            },
                        ],
                    },
                    {
                        // The three ways a new lead can reach you. Text-to-join
                        // keywords joined them here from the former "Engagement"
                        // heading: same page, same URL, same permission — it was
                        // simply never a category of its own to an owner.
                        title: t('nav.headings.leadCapture', 'Get leads'),
                        icon: UserPlus,
                        collapseKey: 'crm-lead-capture',
                        items: [
                            {
                                title: t('nav.leadForms', 'Web forms'),
                                url: ROUTES.FORMS,
                                icon: ClipboardList,
                                requiredPermission: PERMISSION.LEAD_FORMS_READ,
                            },
                            {
                                title: t('nav.joinLinks', 'Sign-up links'),
                                url: ROUTES.JOIN_LINKS,
                                icon: Link2,
                                requiredPermission: 'join-links:read',
                            },
                            {
                                title: t('nav.joinKeywords', 'Text-to-join keywords'),
                                url: ROUTES.JOIN_KEYWORDS,
                                icon: MessageSquareText,
                                requiredPermission: PERMISSION.JOIN_KEYWORDS_READ,
                            },
                        ],
                    },
                ],
            },
            {
                isSection: true,
                // Everything that is an actual conversation with a customer.
                title: t('nav.sections.communication', 'Talk to customers'),
                items: [
                    {
                        title: t('nav.inbox', 'Inbox'),
                        url: ROUTES.INBOX,
                        icon: Inbox,
                        requiredPermission: 'inbox:read',
                    },
                    {
                        // Where customers reach the AI team, and who answers each
                        // channel. Joined the section after Inbox: rename-and-add
                        // only, no group moved.
                        title: t('nav.channelsHub', 'Channels'),
                        url: ROUTES.CHANNELS,
                        icon: Radio,
                        requiredPermission: PERMISSION.CHANNELS_READ,
                    },
                    {
                        title: t('nav.calls', 'Calls'),
                        url: ROUTES.CALLS,
                        icon: Phone,
                        requiredPermission: 'voice-calls:read',
                    },
                    {
                        title: t('nav.tickets', 'Support tickets'),
                        url: ROUTES.TICKETS,
                        icon: Ticket,
                        requiredPermission: 'tickets:read',
                    },
                ],
            },
            {
                isSection: true,
                // The appointments that come out of those conversations. Named
                // after the thing itself, not after the act of scheduling it.
                title: t('nav.sections.planning', 'Bookings'),
                items: [
                    {
                        title: t('nav.groups.scheduling', 'Bookings'),
                        icon: CalendarRange,
                        collapseKey: 'scheduling',
                        items: [
                            {
                                title: t('nav.calendar', 'Calendar'),
                                url: ROUTES.CALENDAR,
                                icon: CalendarDays,
                                requiredPermission: PERMISSION.CALENDAR_READ,
                            },
                            {
                                title: t('nav.bookings', 'Booked appointments'),
                                url: ROUTES.BOOKINGS,
                                icon: CalendarCheck,
                                requiredPermission: PERMISSION.BOOKINGS_READ,
                            },
                            {
                                title: t('nav.meetingTypes', 'Appointment types'),
                                url: ROUTES.MEETING_TYPES,
                                icon: CalendarClock,
                                requiredPermission: PERMISSION.MEETING_TYPES_READ,
                            },
                            {
                                title: t('nav.routingForms', 'Routing forms'),
                                url: ROUTES.ROUTING_FORMS,
                                icon: GitBranch,
                                requiredPermission: PERMISSION.MEETING_TYPES_READ,
                            },
                            {
                                title: t('nav.availability', 'Working hours'),
                                url: ROUTES.AVAILABILITY,
                                icon: Clock,
                                requiredPermission: PERMISSION.AVAILABILITY_MANAGE,
                            },
                        ],
                    },
                ],
            },
            {
                isSection: true,
                // What you send out yourself. The former MARKETING section split
                // in two here: sending is a decision an owner makes, automating
                // is a thing they set up once — and they were never looked for
                // in the same moment.
                title: t('nav.sections.send', 'Send'),
                items: [
                    {
                        title: t('nav.groups.campaigns', 'Campaigns'),
                        icon: Megaphone,
                        collapseKey: 'campaigns',
                        items: [
                            {
                                title: t('nav.campaignsTextEmail', 'Text & email campaigns'),
                                url: ROUTES.CAMPAIGNS,
                                icon: Send,
                                requiredPermission: 'campaigns:read',
                            },
                            {
                                title: t('nav.polls'),
                                url: ROUTES.POLLS,
                                icon: ListChecks,
                                requiredPermission: 'polls:read',
                            },
                            {
                                title: t('nav.templates', 'Message templates'),
                                url: ROUTES.TEMPLATES,
                                icon: FileText,
                                anyOfPermissions: [
                                    PERMISSION.SMS_TEMPLATES_READ,
                                    PERMISSION.EMAIL_TEMPLATES_READ,
                                    PERMISSION.MARKETPLACE_READ,
                                ],
                            },
                        ],
                    },
                    {
                        title: t('nav.groups.advertising', 'Advertising'),
                        icon: Target,
                        collapseKey: 'advertising',
                        items: [
                            {
                                title: t('nav.advertisingGoogle', 'Google Ads'),
                                url: ROUTES.ADVERTISING_GOOGLE,
                                icon: GoogleAdsIcon,
                                requiredPermission: PERMISSION.ADVERTISING_READ,
                            },
                            {
                                title: t('nav.advertisingMeta', 'Meta Ads'),
                                url: ROUTES.ADVERTISING_META,
                                icon: MetaIcon,
                                requiredPermission: PERMISSION.ADVERTISING_READ,
                            },
                            {
                                title: t('nav.advertisingPerformance', 'Performance'),
                                url: ROUTES.ADVERTISING_PERFORMANCE,
                                icon: TrendingUp,
                                requiredPermission: PERMISSION.ADVERTISING_READ,
                            },
                            {
                                title: t('nav.advertisingAccounts', 'Connected Accounts'),
                                url: ROUTES.ADVERTISING_ACCOUNTS,
                                icon: Plug,
                                requiredPermission: PERMISSION.ADVERTISING_READ,
                            },
                        ],
                    },
                ],
            },
            {
                isSection: true,
                // What runs without anybody touching it.
                title: t('nav.sections.automate', 'Automate'),
                items: [
                    {
                        title: t('nav.groups.automation', 'Automations & AI'),
                        icon: Sparkles,
                        collapseKey: 'automation',
                        items: [
                            {
                                // First in the group: the six things an AI team
                                // needs, with progress. Same gate as the checklist.
                                title: t('nav.aiSetup', 'AI Setup'),
                                url: ROUTES.AI_SETUP,
                                icon: ClipboardCheck,
                                requiredPermission: PERMISSION.DASHBOARD_READ,
                            },
                            {
                                // One entry for both: sequences are listed
                                // here too, and "Sequences" left the Campaigns
                                // group above rather than naming the same
                                // rows twice in the sidebar.
                                title: t('nav.advancedAutomations', 'Advanced automations'),
                                url: ROUTES.AUTOMATIONS,
                                icon: Workflow,
                                // EITHER read: the merge moved sequences behind
                                // this one row, so gating on `workflows:read`
                                // alone would take the whole surface away from a
                                // role that has always been allowed to run
                                // sequences. The page itself narrows to the kind
                                // the reader may actually see.
                                anyOfPermissions: [
                                    PERMISSION.WORKFLOWS_READ,
                                    PERMISSION.SEQUENCES_READ,
                                ],
                            },
                            {
                                // The ROLE on the lead screens, which is a
                                // different question from how the capability is
                                // configured — hence its own row beside, not
                                // instead of, My AI Team below.
                                title: t('nav.aiLeadResponder', 'AI Lead Responder'),
                                url: ROUTES.AI_LEAD_RESPONDER,
                                icon: GaugeCircle,
                                // The same read the Activity tab is gated on;
                                // no new permission was minted for the page.
                                requiredPermission: PERMISSION.AI_RUNS_READ,
                            },
                            {
                                title: t('nav.myAiTeam', 'My AI Team'),
                                url: ROUTES.AI_EMPLOYEES,
                                icon: Bot,
                                // `ai-agents:read` is not yet in the shared PERMISSION
                                // catalog (owned by the users-roles stream); the runtime
                                // list carries it for owner/admin/agent.
                                requiredPermission: PERMISSION.AI_AGENTS_READ,
                            },
                            {
                                // The answers the AI draws on — an owner thinks of
                                // this as "what it knows about my business", not
                                // as a store of documents.
                                title: t('nav.knowledgeBases', 'What the AI knows'),
                                url: ROUTES.KNOWLEDGE_BASES,
                                icon: BookOpen,
                                // `knowledge-bases:read` — same not-yet-in-catalog cast
                                // as above.
                                requiredPermission: PERMISSION.KNOWLEDGE_BASES_READ,
                            },
                        ],
                    },
                ],
            },
            {
                isSection: true,
                // What all of it added up to.
                title: t('nav.sections.insights', 'Results'),
                items: [
                    {
                        title: t('nav.groups.analytics', 'Reports'),
                        icon: ChartNoAxesColumn,
                        collapseKey: 'analytics',
                        items: [
                            {
                                // First on purpose: "what happened to the leads
                                // that came in" is the question an owner opens a
                                // report to answer; everything below breaks that
                                // answer down by channel.
                                title: t('nav.outcomes', 'Outcomes'),
                                url: ROUTES.OUTCOMES,
                                icon: Target,
                                requiredPermission: PERMISSION.ANALYTICS_READ,
                            },
                            {
                                title: t('nav.analyticsOverview', 'Overview'),
                                url: ROUTES.ANALYTICS_OVERVIEW,
                                icon: Gauge,
                                requiredPermission: PERMISSION.ANALYTICS_READ,
                            },
                            {
                                title: t('nav.analyticsMarketing', 'Campaign results'),
                                url: ROUTES.ANALYTICS_MARKETING,
                                icon: ChartSpline,
                                requiredPermission: PERMISSION.ANALYTICS_READ,
                            },
                            {
                                title: t('nav.analyticsConversations', 'Conversation results'),
                                url: ROUTES.ANALYTICS_CONVERSATIONS,
                                icon: MessagesSquare,
                                requiredPermission: PERMISSION.ANALYTICS_READ,
                            },
                            {
                                title: t('nav.analyticsRevenue', 'Revenue'),
                                url: ROUTES.ANALYTICS_REVENUE,
                                icon: TrendingUp,
                                requiredPermission: PERMISSION.ANALYTICS_READ,
                            },
                            {
                                title: t('nav.analyticsAppointments', 'Appointment results'),
                                url: ROUTES.ANALYTICS_APPOINTMENTS,
                                icon: CalendarCheck,
                                requiredPermission: PERMISSION.ANALYTICS_READ,
                            },
                            {
                                title: t('nav.analyticsDeliverability', 'Deliverability'),
                                url: ROUTES.ANALYTICS_DELIVERABILITY,
                                icon: ShieldCheck,
                                requiredPermission: PERMISSION.ANALYTICS_READ,
                            },
                        ],
                    },
                ],
            },
            {
                isSection: true,
                title: t('nav.sections.account', 'Account'),
                items: [
                    {
                        title: t('nav.billingPlan', 'Plan & Billing'),
                        url: ROUTES.BILLING,
                        icon: CreditCard,
                        requiredPermission: 'billing:read',
                    },
                    {
                        title: t('nav.settings', 'Settings'),
                        url: ROUTES.SETTINGS,
                        icon: Settings,
                        anyOfPermissions: [
                            PERMISSION.ORGANIZATION_READ,
                            PERMISSION.USERS_READ,
                            PERMISSION.ROLES_READ,
                            PERMISSION.CHANNELS_READ,
                            PERMISSION.OPT_OUT_READ,
                            PERMISSION.OPT_IN_READ,
                            PERMISSION.SETTINGS_READ,
                        ],
                    },
                ],
            },
        ],
        [t, showOnboarding, onboarding, myAgency],
    );

    const filteredRoutes = React.useMemo(() => {
        if (permissionsLoading) return [];
        // Dynamic "Custom records" section (enabled custom modules) before
        // the trailing Account block. Already permission-filtered by its hook.
        const routes = modulesSection
            ? [...allRoutes.slice(0, -1), modulesSection, ...allRoutes.slice(-1)]
            : allRoutes;
        return filterNavItems(routes, hasPermission, hasAnyPermission);
    }, [allRoutes, modulesSection, hasPermission, hasAnyPermission, permissionsLoading]);

    return (
        <Sidebar collapsible="icon" {...props}>
            {/* Branding lives in the footer now (see SidebarFooter below), so
                the header is the workspace switcher alone — one brand lockup
                per sidebar, not two. */}
            <SidebarHeader>
                <TeamSwitcher />
            </SidebarHeader>
            <SidebarSeparator className="mx-0" />
            <SidebarContent>
                {permissionsLoading ? (
                    <div className="flex flex-col gap-2 px-3 py-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Skeleton key={i} className="h-9 w-full rounded-md" />
                        ))}
                    </div>
                ) : (
                    <NavMain items={filteredRoutes} />
                )}
            </SidebarContent>
            {/* The account menu moved to the top-right of the header (see
                `header/user-menu.tsx`); this slot now carries the product
                branding. Hidden in the icon rail, where a 3rem column has no
                room for a wordmark. */}
            <SidebarFooter className="group-data-[collapsible=icon]:hidden">
                <SidebarSeparator className="mx-0" />
                {/* Metrics copied from the org-switcher row in SidebarHeader —
                    h-12, p-2, gap-3, a size-8 mark and the same two-line type
                    scale. The two lockups bookend the sidebar, so anything
                    hand-tuned here reads as misaligned against it. */}
                <div className="flex h-12 items-center gap-3 rounded-md p-2">
                    <img
                        src={
                            resolvedBranding?.source === 'agency' && resolvedBranding.logoUrl
                                ? resolvedBranding.logoUrl
                                : '/logo_apple-touch-icon.png'
                        }
                        alt=""
                        className="size-8 shrink-0 rounded-md object-contain"
                    />
                    {/* `leading-tight` sits on each span, not the wrapper:
                        Tailwind registers `--tw-leading` with `inherits: false`,
                        so a wrapper's leading never reaches `text-sm`/`text-xs`
                        and they keep their paired line-heights (20px + 16px).
                        That made the text block 36px against a 32px mark, which
                        is what left it looking off-centre. Tight on the spans
                        gives 17.5px + 15px, matching the mark. */}
                    <div className="grid min-w-0 flex-1 text-left">
                        <span className="truncate text-sm leading-tight font-medium">
                            {resolvedBranding?.source === 'agency' && resolvedBranding.brandName
                                ? resolvedBranding.brandName
                                : t('brand.name', 'Autocampaign AI')}
                        </span>
                        <span className="truncate text-xs leading-tight text-text-secondary">
                            {resolvedBranding?.source === 'agency'
                                ? (resolvedBranding.supportEmail ??
                                  (resolvedBranding.hideAutocampaignBranding
                                      ? ''
                                      : t(
                                            'brand.tagline',
                                            'The AI-powered CRM for home service businesses',
                                        )))
                                : t(
                                      'brand.tagline',
                                      'The AI-powered CRM for home service businesses',
                                  )}
                        </span>
                    </div>
                </div>
            </SidebarFooter>
            <SidebarRail />
        </Sidebar>
    );
}
